import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import astro from '../../common/lib/astro'
import { FetchesForecast, ForecastCurrentConditions } from '../../components'
import { getAstroValue, timeFormatForUser } from '../../common/ambient'
import './EventCard.less'

const EventCardInner = ({ point, onClick, className, forecast, user }) => {
  const pointDatetime = point?.datetime
  const [countdown, setCountdown] = useState(null)

  // Check if event is today
  const isToday = useMemo(() => {
    if (!pointDatetime) return false
    return moment().isSame(moment(pointDatetime), 'day')
  }, [pointDatetime])

  // Extract forecast data for the event day
  const eventDayForecast = useMemo(() => {
    if (!forecast || !pointDatetime) return null

    // Use current conditions if it's today
    if (isToday && forecast.currently) {
      return forecast.currently
    }

    // Otherwise use daily forecast
    if (!forecast.daily?.data) return null

    const eventDate = moment(pointDatetime).startOf('day').valueOf()
    const dayForecast = forecast.daily.data.find(f =>
      moment(f.time * 1000).startOf('day').valueOf() === eventDate
    )

    return dayForecast
  }, [forecast, pointDatetime, isToday])

  // Calculate sunrise/sunset for event day
  const sunTimes = useMemo(() => {
    if (!forecast || !pointDatetime) return { sunrise: '--', sunset: '--' }

    const getValue = getAstroValue(moment, {
      lat: forecast.lat,
      lon: forecast.lon,
      tz: forecast.tz
    })

    const eventTime = moment(pointDatetime).valueOf()

    return {
      sunrise: getValue(astro.solar.rise, eventTime).format(timeFormatForUser(user)),
      sunset: getValue(astro.solar.set, eventTime).format(timeFormatForUser(user))
    }
  }, [forecast, pointDatetime, user])

  // Update countdown every second
  useEffect(() => {
    if (!pointDatetime) return

    const updateCountdown = () => {
      const now = moment()
      const eventMoment = moment(pointDatetime)
      const diff = eventMoment.diff(now)

      if (diff <= 0) {
        setCountdown(null)
        return
      }

      const duration = moment.duration(diff)
      setCountdown({
        days: Math.floor(duration.asDays()),
        hours: duration.hours(),
        minutes: duration.minutes(),
        seconds: duration.seconds()
      })
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [pointDatetime])

  if (!point) return null

  return (
    <div
      className={classNames('event-card', className)}
      onClick={onClick}
    >
      <div className="top device-device-card-header">
        <div className="meta">
          <div className="avatar-wrap">
          </div>
          <div className="device-name-wrap">
            <span>{point.title}</span>
            {point.datetime && (
              <div className="event-info">
                <span className="info-item">
                  <svg className="icon calendar-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M1 4.5H11" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M3.5 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    <path d="M8.5 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  {moment(point.datetime).format('MMM D, YYYY [at] h:mm A')}
                </span>
              </div>
            )}
            {point.address && (
              <div className="event-info">
                <span className="info-item">
                  <svg className="icon pin-icon" width="10" height="12" viewBox="0 0 10 12" fill="none">
                    <path d="M5 0C2.5 0 0.5 2 0.5 4.5C0.5 7.5 5 12 5 12C5 12 9.5 7.5 9.5 4.5C9.5 2 7.5 0 5 0ZM5 6C4.2 6 3.5 5.3 3.5 4.5C3.5 3.7 4.2 3 5 3C5.8 3 6.5 3.7 6.5 4.5C6.5 5.3 5.8 6 5 6Z" fill="currentColor"/>
                  </svg>
                  {point.address}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-body">
        {countdown && (
          <div className="countdown">
            <div className="countdown-units">
              <div className="countdown-unit">
                <div className="countdown-value">{countdown.days}</div>
                <div className="countdown-label">Days</div>
              </div>
              <div className="countdown-unit">
                <div className="countdown-value">{countdown.hours}</div>
                <div className="countdown-label">Hours</div>
              </div>
              <div className="countdown-unit">
                <div className="countdown-value">{countdown.minutes}</div>
                <div className="countdown-label">Minutes</div>
              </div>
              <div className="countdown-unit">
                <div className="countdown-value">{countdown.seconds}</div>
                <div className="countdown-label">Seconds</div>
              </div>
            </div>
          </div>
        )}
        {eventDayForecast && (
          <div className="card-forecast">
            <ForecastCurrentConditions
              data={eventDayForecast}
              mode={isToday ? 'current' : 'daily'}
              sunTimes={!isToday ? sunTimes : undefined}
              // dailySummary={eventDayForecast.summary}
            />
          </div>
        )}
      </div>

    </div>
  )
}

EventCardInner.propTypes = {
  point: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    datetime: PropTypes.string,
    location: PropTypes.string,
    address: PropTypes.string,
    coordinates: PropTypes.arrayOf(PropTypes.number).isRequired
  }),
  onClick: PropTypes.func,
  className: PropTypes.string,
  forecast: PropTypes.object,
  user: PropTypes.object
}

const EventCard = ({ point, onClick, className, user }) => {
  if (!point || !point.coordinates) return null

  return (
    <FetchesForecast coords={point.coordinates}>
      <EventCardInner
        point={point}
        onClick={onClick}
        className={className}
        user={user}
      />
    </FetchesForecast>
  )
}

EventCard.propTypes = {
  point: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    datetime: PropTypes.string,
    location: PropTypes.string,
    address: PropTypes.string,
    coordinates: PropTypes.arrayOf(PropTypes.number).isRequired
  }),
  onClick: PropTypes.func,
  className: PropTypes.string,
  user: PropTypes.object
}

export default EventCard

EventCard.displayName = 'EventCard'
